import { useState } from "react";
import { CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";
import { services, DEMO_FORM_CONFIRMATION } from "@/data/site";
import { track } from "@/lib/analytics";

// DEMO ONLY: this fictional site does not dispatch service requests.
// The form validates input and shows a demo confirmation — nothing is sent.
const LeadForm = ({ compact = false, defaultService = "" }) => {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: defaultService, message: "" });

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please add your name and phone number.");
      return;
    }
    track("contact_form_submit", { service: form.service || "unspecified", demo: true });
    setSubmitted(true);
    toast.success(DEMO_FORM_CONFIRMATION);
    setForm({ name: "", phone: "", email: "", service: defaultService, message: "" });
  };

  if (submitted) {
    return (
      <div className="border-2 border-[#16A34A] bg-white p-8 text-center" data-testid="lead-form-success">
        <CheckCircle2 className="w-14 h-14 text-[#16A34A] mx-auto mb-4" />
        <h3 className="font-display font-bold text-2xl mb-2">Demo Form Submitted</h3>
        <p className="text-[#475569] mb-6">{DEMO_FORM_CONFIRMATION}</p>
        <button onClick={() => setSubmitted(false)} className="text-sm font-bold uppercase tracking-wide text-[#059669] hover:underline" data-testid="lead-form-reset">
          Test the form again
        </button>
      </div>
    );
  }

  const inputClass = "w-full border-2 border-[#E2E8F0] px-4 py-3 text-base focus:border-[#0F2E4C] focus:outline-none transition-colors bg-white";

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="lead-form">
      <div className={compact ? "space-y-4" : "grid grid-cols-1 sm:grid-cols-2 gap-4"}>
        <div>
          <label className="block text-sm font-bold mb-1.5 uppercase tracking-wide" htmlFor="lf-name">Full Name *</label>
          <input id="lf-name" type="text" value={form.name} onChange={update("name")} placeholder="Jane Doe" className={inputClass} data-testid="lead-name-input" required />
        </div>
        <div>
          <label className="block text-sm font-bold mb-1.5 uppercase tracking-wide" htmlFor="lf-phone">Phone *</label>
          <input id="lf-phone" type="tel" value={form.phone} onChange={update("phone")} placeholder="(212) 555-0100" className={inputClass} data-testid="lead-phone-input" required />
        </div>
      </div>
      <div className={compact ? "space-y-4" : "grid grid-cols-1 sm:grid-cols-2 gap-4"}>
        <div>
          <label className="block text-sm font-bold mb-1.5 uppercase tracking-wide" htmlFor="lf-email">Email</label>
          <input id="lf-email" type="email" value={form.email} onChange={update("email")} placeholder="you@example.com" className={inputClass} data-testid="lead-email-input" />
        </div>
        <div>
          <label className="block text-sm font-bold mb-1.5 uppercase tracking-wide" htmlFor="lf-service">Service Needed</label>
          <select id="lf-service" value={form.service} onChange={update("service")} className={inputClass} data-testid="lead-service-select">
            <option value="">Select a service…</option>
            {services.map((s) => <option key={s.slug} value={s.name}>{s.name}</option>)}
            <option value="Emergency">Emergency Service</option>
            <option value="Other">Other / Not Sure</option>
          </select>
        </div>
      </div>
      <div>
        <label className="block text-sm font-bold mb-1.5 uppercase tracking-wide" htmlFor="lf-message">How can we help?</label>
        <textarea id="lf-message" value={form.message} onChange={update("message")} rows={compact ? 3 : 4} placeholder="Briefly describe the issue or project…" className={inputClass} data-testid="lead-message-input" />
      </div>
      <button type="submit" className="w-full inline-flex items-center justify-center gap-2 bg-[#059669] text-white font-bold uppercase tracking-wide px-6 py-4 text-lg border-2 border-[#059669] hover:bg-[#047857] hover:-translate-y-0.5 transition-transform duration-200" data-testid="lead-submit-btn">
        <Send className="w-5 h-5" /> Request My Free Estimate
      </button>
      <p className="text-xs text-[#475569] text-center">Demo form — no real request is dispatched. This is a fictional business built for demonstration.</p>
    </form>
  );
};

export default LeadForm;
